// Orchestrator for the staff statistics dashboard. Replaces the legacy
// 447-LOC src/pages/staff/VisitorStatistics.js. PR 12 ST 9.
//
// Owns the shared startDate + endDate state used by both ItemStatsTable
// and ExportControls (the "selected date range" applies to both the
// items query and the CSV export). HourlyChart owns its own selectedDate;
// SummaryCards has no date input.
//
// Refresh button invalidates statsKeys.all (covers summary / hourly /
// items / daily-counts / visitors) so each sub-component refetches on
// its own; mirrors the legacy Promise.all(fetch...) pattern but without
// the global loading flag.
//
// Q1 option 1 per project_pr12_cadence: CRA's App.js continues to serve
// /staff/statistics; import path flips to this file.

import { useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@shared/components/ui/button";
import { statsKeys } from "@shared/api/hooks/useStats";

import SummaryCards from "@staff/features/stats/SummaryCards";
import HourlyChart from "@staff/features/stats/HourlyChart";
import ItemStatsTable from "@staff/features/stats/ItemStatsTable";
import ExportControls from "@staff/features/stats/ExportControls";

function localDateString(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const VisitorStatistics: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [startDate, setStartDate] = useState<string>(
    localDateString(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
  );
  const [endDate, setEndDate] = useState<string>(localDateString());
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await queryClient.invalidateQueries({ queryKey: statsKeys.all });
      toast.success("Statistics refreshed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background"
      data-testid="visitor-statistics-page"
    >
      <div className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/staff/exhibitions")}
                variant="ghost"
                size="sm"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-heading font-bold">
                  Visitor Statistics
                </h1>
                <p className="text-sm text-muted-foreground">
                  Track visitor activity and engagement
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={handleRefresh}
                disabled={refreshing}
                variant="outline"
                size="sm"
                data-testid="refresh-button"
              >
                <RefreshCw
                  className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`}
                />
                {refreshing ? "Refreshing..." : "Refresh"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <section className="bg-white border border-border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-6">DX All Time Stats</h2>
          <SummaryCards />
        </section>

        <HourlyChart />

        <ItemStatsTable
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />

        <ExportControls startDate={startDate} endDate={endDate} />
      </div>
    </div>
  );
};

export default VisitorStatistics;
